import { AddOnItemMap, ChargebeeCheckoutItem, ChargebeeSubscriptionInfo, FeatureTierLevel, SpecialTiers, StandardProAddOns, TierItemMap, UsageBasedAddOns } from '../../types';
import { logWithAmplitude, setGoogleSubscriptionId } from '../api/api';

import { State } from './state-management/state';
import accountInfo from './account-info';
import { delay } from '../utils';
import featuresInfo from './features-info';
import { getChargebeeEstimate } from '../chargebee';
import organizationInfo from './organization-info';
import { property } from './state-management/decorators/property';

class SubscriptionInfo extends State {
    @property({ type: Boolean }) isReady: boolean = false;

    @property({ type: String }) subscription_id?: string;

    @property({ type: String }) subscription_tier?: FeatureTierLevel | SpecialTiers | null;

    @property({ type: Number }) subscription_tier_quantity?: number | null;

    @property({ type: Array }) subscription_standard_addons?: StandardProAddOns[] | null;

    @property({ type: Array }) subscription_usage_based_addons?: UsageBasedAddOns[] | null;

    @property({ type: String }) subscription_customer_id?: string | null;

    @property({ type: String }) subscription_plan_id?: string | null;

    @property({ type: Object }) subscription?: ChargebeeSubscriptionInfo;

    @property({ type: Boolean }) hasActiveSubscription: boolean = false;

    @property({ type: Boolean }) isAppSumoUser?: boolean | null;

    @property({ type: Number }) number_of_seats?: number | null;

    static amplitude_callsite: string = 'Subscription Info Cache';

    // call this in sign in page, and then in main-page. in main-page, check if data isReady, if not, instantiate
    instantiateSubscriptionInfo() {
        this.isReady = false;
        this.subscription = organizationInfo.id?.$oid ? organizationInfo.organization?.chargebee_subscription : accountInfo.account?.chargebee_subscription;
        this.hasActiveSubscription = this.subscription?.status === 'active' || this.subscription?.status === 'in_trial';

        // Update with subscription cache?
        this.subscription_id = this.subscription?.subscription_id;
        this.subscription_customer_id = this.subscription?.customer_id ?? null;
        this.subscription_plan_id = this.subscription?.plan_id ?? null;
        this.subscription_tier = TierItemMap[this.subscription?.plan_id ?? ''] ?? null;
        this.subscription_tier_quantity = this.subscription?.num_users ?? 1;
        this.number_of_seats = this.subscription_tier === FeatureTierLevel.Teams ? 6 : this.subscription?.num_users ?? 1;
        this.subscription_standard_addons = this.subscription?.add_ons?.map((addon) => {
            return AddOnItemMap[addon];
        }) ?? null;

        logWithAmplitude(SubscriptionInfo.amplitude_callsite, 'Organization Subscription Status', {
            subscription_id: this.subscription_id,
            chargebee_customer_id: this.subscription_customer_id,
            chargebee_subscription_plan: this.subscription_plan_id,
            login_type: 'organization'
        });

        // important!
        featuresInfo.updateEnabledFeaturesMap();

        this.isReady = true;
        if (this.subscription) {
            setGoogleSubscriptionId(this.subscription?.subscription_id ?? 'Unknown', this.subscription?.status ?? 'Unknown', this.subscription?.plan_id, this.subscription?.customer_id);
        }
    }

    get isTrial(): boolean {
        return this.subscription?.status === 'in_trial';
    }

    get isActive(): boolean {
        return this.subscription?.status === 'active';
    }

    async refreshSubscriptionInfo() {
        await delay(2000);
        await accountInfo.instantiateAccountData();
        if (!accountInfo.isAuthenticated) {
            window.location.reload();
        }
        await organizationInfo.instantiateOrganizationDataAsAuthenticatedUser();
        this.instantiateSubscriptionInfo();
    }

    async getUserIncrementEstimate(numUsers: number) {
        const checkoutItems: ChargebeeCheckoutItem[] = [];
        for (const item of this.subscription?.subscription_items ?? []) {
            const itemId = item.item_price_id;
            if (AddOnItemMap[itemId]) {
                // Increment the quantity by 1 if the item is in the StandardProAddOns
                checkoutItems.push({
                    item_price_id: itemId,
                    quantity: numUsers,
                });
            } else if (TierItemMap[itemId] === FeatureTierLevel.ProBase) {
                // If the item is a tier, set the quantity to the new number of users
                checkoutItems.push({
                    item_price_id: itemId,
                    quantity: numUsers,
                });
            } else {
                // Otherwise, keep the original quantity
                checkoutItems.push({
                    item_price_id: itemId,
                    quantity: item.quantity ?? 1,
                });
            }
        }
        const est =  await getChargebeeEstimate(this.subscription_id, checkoutItems, this.subscription_customer_id ?? undefined, false);
        return est;
    }

    // gotta be a better way to do this
    get subscriptionLevelText(): string {
        if (accountInfo.isAuthenticated) {
            // if organization is defined then set subscription level text to "Teams"
            if (this.subscription_tier === FeatureTierLevel.Teams) {
                return 'Enterprise';
            }
            else if (this.subscription_plan_id === 'Proxi-Team-Member-USD-Yearly') {
                return 'Internal';
            }
            else if (accountInfo.appSumoLicenseDetails?.appsumo_plan_id === 'proxi_tier1') {
                return 'AppSumo Tier 1';
            }
            else if (accountInfo.appSumoLicenseDetails?.appsumo_plan_id === 'proxi_tier2') {
                return 'AppSumo Tier 2';
            }
            else if (accountInfo.appSumoLicenseDetails?.appsumo_plan_id === 'proxi_tier3') {
                return 'AppSumo Tier 3';
            }
            else if (featuresInfo.duplicateMapEnabled) {
                return 'Pro';
            }
            else if (featuresInfo.addMapEditorsEnabled) {
                return 'Starter';
            } else if (organizationInfo.isGuest) {
                return 'Guest';
            }
            else {
                return 'Free';
            }

        } else {
            return 'Free';
        }
    }
}

const subscriptionInfo = new SubscriptionInfo();

export default subscriptionInfo;
